/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\OutboundRouteGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Calls Routing module.
----------------------------------------------------------------*/


import { ArrowRightLeft } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import ModuleDestinationSelect from '../../../components/ui/ModuleDestinationSelect';
import PinListSelect from '../../../components/ui/PinListSelect';
import DialProfileSelect from '../../../components/ui/DialProfileSelect';
import type { OutboundRouteFormState, RoutingStrategy } from '../types';

interface OutboundRouteGeneralSectionProps {
    formData: OutboundRouteFormState;
    errors: Record<string, string>;
    debugModeActive: boolean;
    onFieldChange: <K extends keyof OutboundRouteFormState>(field: K, value: OutboundRouteFormState[K]) => void;
    t: (key: string) => string;
}

export function OutboundRouteGeneralSection({
    formData, errors, debugModeActive, onFieldChange, t,
}: OutboundRouteGeneralSectionProps) {
    return (
        <FormBox title="General Information" icon={<ArrowRightLeft className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

                {/* Name */}
                <Label htmlFor="name">
                    <SimpleTooltip content={t('outboundRoutes.fields.name.tooltip')}>
                        <div className="flex flex-col">
                            <span>
                                {t('outboundRoutes.fields.name.label')}
                                <span className="text-red-500 ml-1">*</span>
                            </span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">name</span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                        placeholder={t('outboundRoutes.fields.name.placeholder')}
                        className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                </div>

                {/* Description */}
                <Label htmlFor="description">
                    <SimpleTooltip content={t('outboundRoutes.fields.description.tooltip')}>
                        <div className="flex flex-col">
                            <span>{t('outboundRoutes.fields.description.label')}</span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">description</span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Input
                        id="description"
                        value={formData.description}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                        placeholder={t('outboundRoutes.fields.description.placeholder')}
                    />
                </div>

                {/* Routing Strategy */}
                <Label htmlFor="routingStrategy">
                    <SimpleTooltip content={t('outboundRoutes.fields.routingStrategy.tooltip')}>
                        <div className="flex flex-col">
                            <span>{t('outboundRoutes.fields.routingStrategy.label')}</span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">routing_strategy</span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div>
                    <select
                        id="routingStrategy"
                        value={formData.routingStrategy}
                        onChange={(e) => onFieldChange('routingStrategy', e.target.value as RoutingStrategy)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        <option value="failover">{t('outboundRoutes.strategies.failover')}</option>
                        <option value="round-robin">{t('outboundRoutes.strategies.roundRobin')}</option>
                        <option value="load-balance">{t('outboundRoutes.strategies.loadBalance')}</option>
                        <option value="lcr">{t('outboundRoutes.strategies.lcr')}</option>
                    </select>
                </div>

                {/* PIN List */}
                <Label htmlFor="pinListId">
                    <SimpleTooltip content={t('outboundRoutes.fields.pinList.tooltip')}>
                        <div className="flex flex-col">
                            <span>{t('outboundRoutes.fields.pinList.label')}</span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">pin_list_id</span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div>
                    <PinListSelect
                        value={formData.pinListId}
                        onChange={(value) => onFieldChange('pinListId', value)}
                        placeholder={t('outboundRoutes.fields.pinList.placeholder')}
                    />
                </div>

                {/* Dial Profile */}
                <Label htmlFor="dialProfileId">
                    <SimpleTooltip content={t('outboundRoutes.fields.dialProfile.tooltip')}>
                        <div className="flex flex-col">
                            <span>{t('outboundRoutes.fields.dialProfile.label')}</span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">dial_profile_id</span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div>
                    <DialProfileSelect
                        value={formData.dialProfileId}
                        onChange={(value) => onFieldChange('dialProfileId', value)}
                        placeholder={t('outboundRoutes.fields.dialProfile.placeholder')}
                        nullLabel={t('outboundRoutes.fields.dialProfile.nullLabel')}
                        allowNull
                    />
                </div>

                {/* Enabled / Status (Toggle) */}
                <Label htmlFor="enabled">
                    <SimpleTooltip content={t('outboundRoutes.fields.enabled.tooltip')}>
                        <div className="flex flex-col">
                            <span>{t('outboundRoutes.fields.enabled.label')}</span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">enabled</span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div>
                    <Toggle
                        value={formData.enabled}
                        onChange={(value) => onFieldChange('enabled', value)}
                        trueLabel={t('common.yes')}
                        falseLabel={t('common.no')}
                    />
                </div>

                {/* Failover Destination */}
                <Label htmlFor="failoverDestination">
                    <SimpleTooltip content={t('outboundRoutes.fields.failoverDestination.tooltip')}>
                        <div className="flex flex-col">
                            <span>{t('outboundRoutes.fields.failoverDestination.label')}</span>
                            {debugModeActive && (
                                <span className="text-xs text-muted-foreground mt-0.5">
                                    failover_module / failover_destination
                                </span>
                            )}
                        </div>
                    </SimpleTooltip>
                </Label>
                <div className="col-span-3">
                    <ModuleDestinationSelect
                        moduleValue={formData.failoverModule || ''}
                        destinationValue={formData.failoverDestination || ''}
                        onModuleChange={(value) => onFieldChange('failoverModule', value || '')}
                        onDestinationChange={(value) => onFieldChange('failoverDestination', value || '')}
                        modulePlaceholder={t('outboundRoutes.fields.failoverModule.placeholder')}
                        destinationPlaceholder={t('outboundRoutes.fields.failoverDestination.placeholder')}
                    />
                </div>

            </div>
        </FormBox>
    );
}
